import { AUDIO_BITRATE, AUDIO_SAMPLE_RATE, VIDEO_RESOLUTIONS, videoBitrate, type ExportMethod, type VideoCodec, type VideoResolution } from './frames';

/**
 * What this browser can export (Phase 4B). Everything is detected at runtime: nothing is
 * assumed from the browser's name, and a codec is offered only when the encoder itself
 * confirms the exact configuration.
 */

/** WebCodecs codec strings (VP9 profile 0, level 1.0, 8-bit; the encoder raises the level as needed). */
export const CODEC_STRINGS: Record<VideoCodec, string> = { vp8: 'vp8', vp9: 'vp09.00.10.08' };

export const RECORDER_TYPES: Record<VideoCodec, string> = { vp8: 'video/webm;codecs=vp8,opus', vp9: 'video/webm;codecs=vp9,opus' };

export interface ExportCapabilities {
  /** Frame-accurate offline export: video codecs confirmed per resolution. */
  offline: { available: boolean; codecs: Record<VideoCodec, VideoResolution[]>; opus: boolean; missing: string[] };
  /** Real-time recording fallback: MediaRecorder WebM types. */
  realtime: { available: boolean; codecs: VideoCodec[]; missing: string[] };
}

/** The browser APIs capability detection reads (injectable for tests). */
export interface MediaApis {
  VideoEncoder?: { isConfigSupported(config: VideoEncoderConfig): Promise<{ supported?: boolean }> };
  AudioEncoder?: { isConfigSupported(config: AudioEncoderConfig): Promise<{ supported?: boolean }> };
  VideoFrame?: unknown;
  AudioData?: unknown;
  MediaRecorder?: { isTypeSupported(type: string): boolean };
  canvasCaptureStream: boolean;
  AudioContext?: unknown;
  OfflineAudioContext?: unknown;
}

export function browserMediaApis(): MediaApis {
  const g = globalThis as unknown as Record<string, unknown>;
  return {
    VideoEncoder: g.VideoEncoder as MediaApis['VideoEncoder'],
    AudioEncoder: g.AudioEncoder as MediaApis['AudioEncoder'],
    VideoFrame: g.VideoFrame,
    AudioData: g.AudioData,
    MediaRecorder: g.MediaRecorder as MediaApis['MediaRecorder'],
    canvasCaptureStream: typeof HTMLCanvasElement !== 'undefined' && typeof HTMLCanvasElement.prototype.captureStream === 'function',
    AudioContext: g.AudioContext,
    OfflineAudioContext: g.OfflineAudioContext,
  };
}

export function videoEncoderConfig(codec: VideoCodec, resolution: VideoResolution, fps: number, bitrate = videoBitrate(resolution, fps, 'standard')): VideoEncoderConfig {
  const { width, height } = VIDEO_RESOLUTIONS[resolution];
  return { codec: CODEC_STRINGS[codec], width, height, framerate: fps, bitrate, bitrateMode: 'variable', latencyMode: 'quality' };
}

export const opusConfig = (): AudioEncoderConfig => ({ codec: 'opus', sampleRate: AUDIO_SAMPLE_RATE, numberOfChannels: 2, bitrate: AUDIO_BITRATE });

async function supported(check: () => Promise<{ supported?: boolean }>): Promise<boolean> {
  try {
    return (await check()).supported === true;
  } catch {
    return false;
  }
}

export async function detectCapabilities(apis: MediaApis = browserMediaApis()): Promise<ExportCapabilities> {
  const missing: string[] = [];
  if (!apis.OfflineAudioContext) missing.push('OfflineAudioContext');
  if (!apis.VideoEncoder) missing.push('VideoEncoder');
  if (!apis.VideoFrame) missing.push('VideoFrame');
  if (!apis.AudioEncoder) missing.push('AudioEncoder');
  if (!apis.AudioData) missing.push('AudioData');
  const codecs: Record<VideoCodec, VideoResolution[]> = { vp8: [], vp9: [] };
  let opus = false;
  if (apis.VideoEncoder && apis.VideoFrame) {
    for (const codec of ['vp8', 'vp9'] as const) {
      for (const resolution of Object.keys(VIDEO_RESOLUTIONS) as VideoResolution[]) {
        if (await supported(() => apis.VideoEncoder!.isConfigSupported(videoEncoderConfig(codec, resolution, 30)))) codecs[codec].push(resolution);
      }
    }
    if (codecs.vp8.length === 0 && codecs.vp9.length === 0) missing.push('a VP8 or VP9 video encoder');
  }
  if (apis.AudioEncoder && apis.AudioData) {
    opus = await supported(() => apis.AudioEncoder!.isConfigSupported(opusConfig()));
    if (!opus) missing.push('an Opus audio encoder');
  }
  const offlineAvailable = missing.length === 0;

  const rtMissing: string[] = [];
  if (!apis.OfflineAudioContext) rtMissing.push('OfflineAudioContext');
  if (!apis.AudioContext) rtMissing.push('AudioContext');
  if (!apis.canvasCaptureStream) rtMissing.push('canvas.captureStream');
  const rtCodecs: VideoCodec[] = [];
  if (!apis.MediaRecorder) rtMissing.push('MediaRecorder');
  else {
    for (const codec of ['vp8', 'vp9'] as const) {
      try {
        if (apis.MediaRecorder.isTypeSupported(RECORDER_TYPES[codec])) rtCodecs.push(codec);
      } catch {
        // treated as unsupported
      }
    }
    if (rtCodecs.length === 0) rtMissing.push('WebM (VP8/VP9 + Opus) recording');
  }
  return {
    offline: { available: offlineAvailable, codecs, opus, missing },
    realtime: { available: rtMissing.length === 0, codecs: rtCodecs, missing: rtMissing },
  };
}

/** Codecs offered for a method and resolution, preferred first (VP8: fastest in software). */
export function codecsFor(caps: ExportCapabilities, method: ExportMethod, resolution: VideoResolution): VideoCodec[] {
  if (method === 'offline') return caps.offline.available ? (['vp8', 'vp9'] as const).filter((c) => caps.offline.codecs[c].includes(resolution)) : [];
  return caps.realtime.available ? [...caps.realtime.codecs] : [];
}

/** The default method: frame-accurate offline export when possible, else real-time recording, else none. */
export function defaultMethod(caps: ExportCapabilities): ExportMethod | null {
  if (caps.offline.available) return 'offline';
  if (caps.realtime.available) return 'realtime';
  return null;
}

export const formatLabel = (codec: VideoCodec): string => `WebM · ${codec.toUpperCase()} + Opus`;
